/**
 * Chat-attachment domain (file/audio/website uploads feeding an assistant's
 * knowledge base) - built on apiClient via useRequest, not useAppServices().
 * Split out from queries/assistants.js because these are transport/upload
 * concerns for a chat/knowledge-base file, not assistant CRUD. Shared by:
 *   - layouts/super-admin/Assistants/index.js (file upload, audio upload,
 *     knowledge-base file delete)
 *   - Assistants/Components/AddAWebsite/index.js (website upload)
 *   - Assistants/Components/CreateModal/index.js (multimedia upload, used
 *     while composing a new assistant)
 *   - Assistants/Components/KBFiles/KnowledgeBaseFiles.js (file delete,
 *     file download)
 *
 * useUploadAssistantFile/useUploadAssistantAudio/useSaveAssistantMultimedia/
 * useDownloadAssistantFile return a plain `(arg, opts) => request(...)`
 * function rather than a TanStack mutation object - none of these were
 * wrapped in useMutation/useQuery in the old code either (callers already
 * manage their own loading/UI state and call onLoad()/refresh manually), so
 * this preserves that shape instead of forcing mutation semantics onto them.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";

const { GET, POST, DELETE } = ReqMethods;

export function useUploadAssistantFile() {
  const request = useRequest();
  return (payload, opts) => request(POST, "/chat/assistants/files/upload", { payload, ...opts });
}

export function useUploadAssistantAudio() {
  const request = useRequest();
  return (payload, opts) => request(POST, "/chat/audio", { payload, ...opts });
}

export function useSaveAssistantMultimedia() {
  const request = useRequest();
  return (payload, opts) => request(POST, "/chat/multimedia", { payload, ...opts });
}

export function useDownloadAssistantFile() {
  const request = useRequest();
  return (fileId, opts) =>
    request(GET, "/chat/assistants/files/download", { query: `file_id=${fileId}`, ...opts });
}

// No built-in onSuccess: Assistants/index.js's delete refreshes both
// ["assistants"] and ["knowledge-base"], KnowledgeBaseFiles.js's only
// refreshes ["knowledge-base"] - each caller passes its own onSuccess to
// mutate/mutateAsync so both keep their pre-migration invalidation exactly.
export function useDeleteAssistantFile() {
  const request = useRequest();
  return useMutation({
    mutationFn: ({ fileId, assistantId }) =>
      request(DELETE, "/chat/assistants/files/delete", {
        query: `file_id=${fileId}&assistant_id=${assistantId}`,
        toaster: true,
      }),
  });
}

export function useUploadAssistantWebsite() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/chat/assistants/website/upload", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["assistants"] }),
  });
}
